import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'accepted', label: 'Aceites' },
  { key: 'picked_up', label: 'Em Curso' },
  { key: 'delivered', label: 'Concluídos' },
  { key: 'removed', label: 'Eliminados' },
];

const STATUS = {
  pending: ['Pendente', 'bg-yellow-100 text-yellow-800'],
  accepted: ['Aceite', 'bg-blue-100 text-blue-800'],
  picked_up: ['Em Curso', 'bg-purple-100 text-purple-800'],
  delivered: ['Entregue', 'bg-green-100 text-green-800'],
  cancelled: ['Cancelado', 'bg-red-100 text-red-800'],
};

const addressLine = (a) => (a ? [[a.street, a.number].filter(Boolean).join(' '), a.city].filter(Boolean).join(', ') : '-');

const OrderRow = ({ order, removedView, busy, onRemove, onRestore }) => {
  const [label, color] = STATUS[order.status] || STATUS.pending;
  return (
    <tr className="hover:bg-gray-50" data-testid={`admin-order-row-${order.id}`}>
      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">{order.id.slice(0, 8)}</td>
      <td className="px-4 py-3 text-sm">{order.customer_name}</td>
      <td className="px-4 py-3 text-sm">{order.driver_name || '-'}</td>
      <td className="px-4 py-3 text-xs text-gray-600 min-w-[220px]">
        <div><span className="font-medium text-gray-800">De:</span> {addressLine(order.pickup_address)}</div>
        <div><span className="font-medium text-gray-800">Para:</span> {addressLine(order.delivery_address)}</div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`px-2 py-1 text-xs rounded-full ${color}`}>{label}</span>
        {removedView && order.status_before_removal && (
          <div className="text-[11px] text-gray-500 mt-1">antes: {(STATUS[order.status_before_removal] || [])[0]}</div>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm">€{(order.final_price ?? order.estimated_price)?.toFixed(2) || 'N/A'}</td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString('pt-PT')}</td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        {removedView ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onRestore(order)} data-testid={`admin-restore-order-${order.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />} Repor
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={busy} onClick={() => onRemove(order)} data-testid={`admin-remove-order-${order.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Eliminar
          </Button>
        )}
      </td>
    </tr>
  );
};

export const AdminOrders = () => {
  const { token } = useAuth();
  const [filter, setFilter] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toRemove, setToRemove] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'removed' ? { removed: true } : (filter === 'all' ? {} : { status: filter });
      const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` }, params });
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [filter, token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const runAction = async (order, action, successMsg) => {
    setBusyId(order.id);
    try {
      await axios.post(`${API}/admin/orders/${order.id}/${action}`, {}, { headers });
      toast.success(successMsg);
      setOrders(prev => prev.filter(o => o.id !== order.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operação falhou');
    } finally {
      setBusyId(null);
    }
  };

  const removedView = filter === 'removed';

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="text-2xl font-bold">Gestão de Pedidos</h2>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(f => (
            <Button
              key={f.key}
              onClick={() => setFilter(f.key)}
              variant={filter === f.key ? 'default' : 'outline'}
              className={filter === f.key ? 'bg-tuggo-mint hover:bg-tuggo-mint-dark text-white' : ''}
              data-testid={`admin-orders-filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>
      {removedView && (
        <p className="text-sm text-gray-600">Pedidos eliminados não aparecem a clientes nem a estafetas. Pode repô-los no estado anterior.</p>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-12 text-center text-gray-500">A carregar...</div>
        ) : orders.length === 0 ? (
          <div className="py-12 text-center text-gray-500" data-testid="admin-orders-empty">Sem pedidos nesta categoria</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['ID', 'Cliente', 'Estafeta', 'Moradas', 'Estado', 'Preço', 'Data', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map(order => (
                <OrderRow
                  key={order.id}
                  order={order}
                  removedView={removedView}
                  busy={busyId === order.id}
                  onRemove={setToRemove}
                  onRestore={(o) => runAction(o, 'restore', 'Pedido reposto')}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!toRemove} onOpenChange={(open) => !open && setToRemove(null)}>
        <AlertDialogContent data-testid="admin-remove-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar este pedido?</AlertDialogTitle>
            <AlertDialogDescription>
              O pedido {toRemove?.id.slice(0, 8)} de {toRemove?.customer_name} fica cancelado e deixa de aparecer ao cliente e aos estafetas. Pode repô-lo mais tarde em "Eliminados".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-remove-order-cancel">Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => { runAction(toRemove, 'remove', 'Pedido eliminado'); setToRemove(null); }}
              data-testid="admin-remove-order-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
